import { Link } from 'react-router-dom';
import SiteNavbar from '@/components/feature/SiteNavbar';
import SiteFooter from '@/components/feature/SiteFooter';
import { nomoCharacters } from '@/mocks/characters';
import { useScrollReveal } from '@/hooks/useScrollReveal';

const toneMap: Record<string, { chip: string; text: string; ring: string }> = {
  primary: {
    chip: 'bg-primary-100 text-primary-700',
    text: 'text-primary-600',
    ring: 'border-primary-200',
  },
  accent: {
    chip: 'bg-accent-100 text-accent-700',
    text: 'text-accent-700',
    ring: 'border-accent-200',
  },
  secondary: {
    chip: 'bg-secondary-100 text-secondary-700',
    text: 'text-secondary-700',
    ring: 'border-secondary-200',
  },
};

export default function CharactersPage() {
  useScrollReveal();
  const [featured, ...rest] = nomoCharacters;
  const featuredTone = toneMap[featured.tone];

  return (
    <div className="min-h-screen w-full bg-background-50">
      <SiteNavbar />
      <main>
        <section className="bg-background-100 pb-16 pt-28 md:pb-24 md:pt-40">
          <div className="mx-auto max-w-6xl px-4 md:px-6">
            <nav className="reveal mb-8 flex items-center gap-2 text-xs text-foreground-500" aria-label="面包屑">
              <Link
                to="/"
                className="transition-colors duration-300 hover:text-primary-600 cursor-pointer"
              >
                首页
              </Link>
              <i className="ri-arrow-right-s-line text-foreground-300"></i>
              <span className="text-foreground-800">IP 角色</span>
            </nav>

            <div className="reveal">
              <div className="mb-6 flex items-center gap-3">
                <span className="text-[11px] tracking-[0.34em] text-primary-600 uppercase">
                  The NOMOISM Characters
                </span>
                <span className="h-px w-8 bg-primary-300"></span>
                <span className="text-[11px] tracking-[0.28em] text-foreground-400 uppercase">
                  一味 × 一境 × 一愿
                </span>
              </div>
              <h1 className="font-heading text-4xl leading-tight tracking-[0.06em] text-foreground-950 md:text-6xl">
                NOMOISM UNIVERSE
              </h1>
              <p className="mt-6 max-w-2xl text-sm leading-relaxed text-foreground-600 md:text-base">
                每一个 IP，都是一个来自中国不同地方的小生命。它有自己的味道、自己的风土，和想替你实现的一个愿望。
                点击任意一个角色，走进它完整的世界。
              </p>
              <p className="mt-4 text-xs tracking-[0.2em] text-foreground-400 uppercase">
                {nomoCharacters.length} NOMO 灵 · 从东到西，从南到北
              </p>
            </div>
          </div>
        </section>

        <section className="bg-background-50 py-14 md:py-20">
          <div className="mx-auto max-w-6xl px-4 md:px-6">
            <Link
              to={`/character/${featured.id}`}
              className="reveal group block overflow-hidden rounded-lg border border-background-200/70 bg-background-50 transition-colors duration-300 hover:border-primary-200"
            >
              <div className="grid grid-cols-1 lg:grid-cols-2">
                <div className="relative h-[300px] w-full bg-background-100 md:h-[440px] lg:h-full lg:min-h-[440px]">
                  <img
                    src={featured.image}
                    alt={`${featured.name} NOMOISM 首个 NOMO 灵潮玩角色`}
                    title={`NOMOISM ${featured.name} · ${featured.place}`}
                    className="h-full w-full object-cover object-top transition-transform duration-700 group-hover:scale-[1.03]"
                  />
                  <span className="absolute left-5 top-5 rounded-full bg-primary-500 px-3.5 py-1.5 text-[11px] tracking-[0.16em] text-background-50">
                    首个 NOMO 灵
                  </span>
                </div>

                <div className="flex flex-col justify-center p-6 md:p-10">
                  <p className="text-[11px] tracking-[0.28em] text-foreground-400 uppercase">
                    {featured.placeEn}
                  </p>
                  <h2 className="mt-3 font-heading text-3xl text-foreground-950 md:text-4xl">
                    {featured.name}
                    <span className="ml-3 text-sm tracking-[0.2em] text-foreground-400 uppercase">
                      {featured.enName}
                    </span>
                  </h2>
                  <p className="mt-4 font-heading text-lg text-foreground-800">{featured.tagline}</p>
                  <p className="mt-4 text-sm leading-relaxed text-foreground-600">{featured.origin}</p>

                  <div className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-3">
                    <div className="rounded-md bg-primary-50 px-4 py-3">
                      <p className="text-[11px] tracking-[0.2em] text-primary-600 uppercase">Flavor</p>
                      <p className="mt-1 text-sm text-foreground-800">{featured.flavor}</p>
                    </div>
                    <div className="rounded-md bg-secondary-50 px-4 py-3">
                      <p className="text-[11px] tracking-[0.2em] text-secondary-700 uppercase">Place</p>
                      <p className="mt-1 text-sm text-foreground-800">{featured.place}</p>
                    </div>
                    <div className="rounded-md bg-accent-50 px-4 py-3">
                      <p className="text-[11px] tracking-[0.2em] text-accent-700 uppercase">Wish</p>
                      <p className="mt-1 text-sm text-foreground-800">{featured.wish}</p>
                    </div>
                  </div>

                  <span className="mt-8 inline-flex items-center gap-2 text-sm text-primary-600">
                    进入 {featured.name} 的世界
                    <i className="ri-arrow-right-line transition-transform duration-300 group-hover:translate-x-1"></i>
                  </span>
                </div>
              </div>
            </Link>

            <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
              {rest.map((character, index) => {
                const tone = toneMap[character.tone];
                return (
                  <Link
                    key={character.id}
                    to={`/character/${character.id}`}
                    className="reveal group block overflow-hidden rounded-lg border border-background-200/70 bg-background-50 transition-colors duration-300 hover:border-primary-200"
                    style={{ transitionDelay: `${index * 100}ms` }}
                  >
                    <div className="relative h-[260px] w-full overflow-hidden bg-background-100">
                      <img
                        src={character.image}
                        alt={`${character.name} NOMOISM 东方潮玩角色`}
                        title={`NOMOISM ${character.name} · ${character.flavor}`}
                        className="h-full w-full object-cover object-top transition-transform duration-700 group-hover:scale-[1.05]"
                      />
                      <span className="absolute left-4 top-4 rounded-full bg-foreground-950/70 px-3 py-1 text-[10px] tracking-[0.14em] text-background-50">
                        待登场
                      </span>
                    </div>
                    <div className="p-5">
                      <div className="flex items-center justify-between gap-2">
                        <h3 className="font-heading text-lg text-foreground-950">{character.name}</h3>
                        <span className={`rounded-full px-2.5 py-1 text-[10px] tracking-[0.12em] ${tone.chip}`}>
                          {character.wish.split(' · ')[0]}
                        </span>
                      </div>
                      <p className="mt-1 text-[11px] tracking-[0.16em] text-foreground-400 uppercase">
                        {character.placeEn}
                      </p>
                      <p className="mt-3 text-sm leading-relaxed text-foreground-600">
                        {character.tagline}
                      </p>
                      <p className={`mt-4 text-xs ${tone.text}`}>一味 · {character.flavor}</p>
                      <span className="mt-4 inline-flex items-center gap-2 text-sm text-primary-600">
                        走进它的世界
                        <i className="ri-arrow-right-line text-sm transition-transform duration-300 group-hover:translate-x-1"></i>
                      </span>
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}